import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getSales, getSalons, deleteSale, exportCSV, exportPDFData } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  CalendarIcon, 
  Plus, 
  Pencil, 
  Trash2, 
  Download,
  FileText,
  Search,
  Filter
} from "lucide-react";
import { cn } from "../lib/utils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const Reports = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSalon, setSelectedSalon] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchSalons();
    fetchSales();
  }, []);

  useEffect(() => {
    fetchSales();
  }, [selectedSalon, startDate, endDate]);

  const fetchSalons = async () => {
    try {
      const response = await getSalons();
      setSalons(response.data);
    } catch (error) {
      console.error("Failed to fetch salons:", error);
    }
  };

  const fetchSales = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedSalon !== "all") {
        params.salon_id = selectedSalon;
      }
      if (startDate) {
        params.start_date = format(startDate, "yyyy-MM-dd");
      }
      if (endDate) {
        params.end_date = format(endDate, "yyyy-MM-dd");
      }
      const response = await getSales(params);
      setSales(response.data);
    } catch (error) {
      toast.error("Failed to fetch sales data");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteSale(deleteId);
      toast.success("Sale record deleted");
      fetchSales();
    } catch (error) {
      toast.error("Failed to delete record");
    } finally {
      setDeleteId(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = {};
      if (selectedSalon !== "all") params.salon_id = selectedSalon;
      if (startDate) params.start_date = format(startDate, "yyyy-MM-dd");
      if (endDate) params.end_date = format(endDate, "yyyy-MM-dd");
      
      const response = await exportCSV(params);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `salon_sales_report_${format(new Date(), "yyyy-MM-dd")}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = {};
      if (selectedSalon !== "all") params.salon_id = selectedSalon;
      if (startDate) params.start_date = format(startDate, "yyyy-MM-dd");
      if (endDate) params.end_date = format(endDate, "yyyy-MM-dd");
      
      const response = await exportPDFData(params);
      const salesData = response.data.sales;
      
      const doc = new jsPDF({ orientation: "landscape" });
      
      doc.setFontSize(18);
      doc.text("Salon Sales Report", 14, 22);
      doc.setFontSize(11);
      doc.text(`Generated: ${format(new Date(), "PPP")}`, 14, 30);
      
      const tableData = salesData.map(s => [
        format(new Date(s.date), "MMM d, yyyy"),
        s.salon_name || "",
        `₹${s.opening_balance.toLocaleString()}`,
        `₹${s.service_sale.toLocaleString()}`,
        `₹${s.product_sale.toLocaleString()}`,
        `₹${s.membership_sale.toLocaleString()}`,
        `₹${s.misc_sale.toLocaleString()}`,
        `₹${s.card_sale.toLocaleString()}`,
        `₹${s.today_total.toLocaleString()}`,
        `₹${s.cash_expenses.toLocaleString()}`,
        `₹${(s.bank_deposit || 0).toLocaleString()}`,
        `₹${s.closing_balance.toLocaleString()}`,
        `${s.male_walkins}/${s.female_walkins}`,
      ]);

      autoTable(doc, {
        head: [["Date", "Salon", "OB", "Service", "Product", "Membership", "Misc", "Card", "Total", "Expenses", "Bank Dep.", "Closing", "Walk-ins"]],
        body: tableData,
        startY: 38,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [45, 74, 67] },
      });

      doc.save(`salon_sales_report_${format(new Date(), "yyyy-MM-dd")}.pdf`);
      toast.success("PDF exported successfully");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const clearFilters = () => {
    setSelectedSalon("all");
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Sales Reports</h1>
          <p className="text-muted-foreground mt-1">View and manage all daily sales records</p>
        </div>
        <Button 
          onClick={() => navigate("/sales")} 
          className="rounded-full"
          data-testid="new-sale-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Sale Entry
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Filter className="w-5 h-5 text-primary" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Salon</label>
              <Select value={selectedSalon} onValueChange={setSelectedSalon} data-testid="filter-salon">
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Salons" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Salons</SelectItem>
                  {salons.map((salon) => (
                    <SelectItem key={salon.id} value={salon.id}>{salon.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Start Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-[160px] justify-start text-left font-normal", !startDate && "text-muted-foreground")}
                    data-testid="filter-start-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "MMM d, yyyy") : "From"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">End Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-[160px] justify-start text-left font-normal", !endDate && "text-muted-foreground")}
                    data-testid="filter-end-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "MMM d, yyyy") : "To"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <Button variant="ghost" onClick={clearFilters} className="rounded-full">
              Clear Filters
            </Button>

            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={handleExportCSV} className="rounded-full" data-testid="export-csv">
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button variant="outline" onClick={handleExportPDF} className="rounded-full" data-testid="export-pdf">
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border/50 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : sales.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="text-left py-4 px-4 font-semibold text-slate-700">Date</th>
                    <th className="text-left py-4 px-4 font-semibold text-slate-700">Salon</th>
                    <th className="text-right py-4 px-4 font-semibold text-slate-700">Opening</th>
                    <th className="text-right py-4 px-4 font-semibold text-slate-700">Total Sale</th>
                    <th className="text-right py-4 px-4 font-semibold text-slate-700">Card</th>
                    <th className="text-right py-4 px-4 font-semibold text-slate-700">Cash</th>
                    <th className="text-right py-4 px-4 font-semibold text-slate-700">Expenses</th>
                    <th className="text-right py-4 px-4 font-semibold text-slate-700">Bank Dep.</th>
                    <th className="text-right py-4 px-4 font-semibold text-slate-700">Closing</th>
                    <th className="text-center py-4 px-4 font-semibold text-slate-700">Walk-ins</th>
                    <th className="text-center py-4 px-4 font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id} className="border-b border-border/50 hover:bg-accent/50 transition-colors">
                      <td className="py-4 px-4 font-medium">{format(new Date(sale.date), "MMM d, yyyy")}</td>
                      <td className="py-4 px-4">{sale.salon_name}</td>
                      <td className="py-4 px-4 text-right">{formatCurrency(sale.opening_balance)}</td>
                      <td className="py-4 px-4 text-right font-semibold">{formatCurrency(sale.today_total)}</td>
                      <td className="py-4 px-4 text-right text-blue-600">{formatCurrency(sale.card_sale)}</td>
                      <td className="py-4 px-4 text-right text-green-600">{formatCurrency(sale.today_total - sale.card_sale)}</td>
                      <td className="py-4 px-4 text-right text-red-600">{formatCurrency(sale.cash_expenses)}</td>
                      <td className="py-4 px-4 text-right text-orange-600">{formatCurrency(sale.bank_deposit || 0)}</td>
                      <td className={cn(
                        "py-4 px-4 text-right font-semibold",
                        sale.closing_balance >= 0 ? "text-primary" : "text-red-600"
                      )}>
                        {formatCurrency(sale.closing_balance)}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-blue-600">M:{sale.male_walkins}</span>
                        {" / "}
                        <span className="text-pink-600">F:{sale.female_walkins}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/sales/${sale.id}`)}
                            className="h-8 w-8 rounded-full"
                            data-testid={`edit-sale-${sale.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteId(sale.id)}
                            className="h-8 w-8 rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-sale-${sale.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <FileText className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No sales records found</p>
              <p className="text-sm mt-1">Start by adding your first daily sale entry</p>
              <Button onClick={() => navigate("/sales")} className="mt-4 rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Sale Entry
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sale Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this sale record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Reports;
